import { z as zod } from 'zod';
import { useMemo, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import InputAdornment from '@mui/material/InputAdornment';

import { useBoolean } from 'src/hooks/use-boolean';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';

import { useAuthContext } from 'src/auth/hooks';
import { changeUserPassword } from 'src/services/api';

// ----------------------------------------------------------------------

export const ChangePasswordSchema = zod
  .object({
    oldPassword: zod.string().min(1, { message: 'Old password is required!' }),
    newPassword: zod
      .string()
      .min(1, { message: 'New password is required!' })
      .min(6, { message: 'Password must be at least 6 characters!' }),
    confirmPassword: zod.string().min(1, { message: 'Confirm password is required!' }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match!',
    path: ['confirmPassword'],
  })
  .refine((data) => data.oldPassword !== data.newPassword, {
    message: 'New password must differ from the old one!',
    path: ['newPassword'],
  });

// ----------------------------------------------------------------------

export function ChangePasswordDialog({ open, onClose }) {
  const passwordVisible = useBoolean();

  const { user } = useAuthContext();

  const defaultValues = useMemo(
    () => ({ oldPassword: '', newPassword: '', confirmPassword: '' }),
    []
  );

  const methods = useForm({
    mode: 'all',
    resolver: zodResolver(ChangePasswordSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset, defaultValues]);

  const handleClose = useCallback(() => {
    reset(defaultValues);
    onClose();
  }, [defaultValues, onClose, reset]);

  const onSubmit = handleSubmit(async ({ oldPassword, newPassword }) => {
    try {
      const change = changeUserPassword({ userName: user?.name, oldPassword, newPassword });

      toast.promise(change, {
        loading: 'Loading...',
        success: 'Password changed successfully!',
        error: (err) => err.description || 'Password change error!',
      });

      await change;
      handleClose();
    } catch (error) {
      console.error(error);
    }
  });

  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton onClick={passwordVisible.onToggle} edge="end">
        <Iconify icon={passwordVisible.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
      </IconButton>
    </InputAdornment>
  );

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { maxWidth: 480 } }}
    >
      <Form methods={methods} onSubmit={onSubmit}>
        <DialogTitle>Change password</DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Field.Text
              name="oldPassword"
              label="Old Password"
              type={passwordVisible.value ? 'text' : 'password'}
              InputLabelProps={{ shrink: true }}
              InputProps={{ endAdornment: passwordAdornment }}
            />

            <Field.Text
              name="newPassword"
              label="New Password"
              placeholder="6+ characters"
              type={passwordVisible.value ? 'text' : 'password'}
              InputLabelProps={{ shrink: true }}
              InputProps={{ endAdornment: passwordAdornment }}
            />

            <Field.Text
              name="confirmPassword"
              label="Confirm New Password"
              type={passwordVisible.value ? 'text' : 'password'}
              InputLabelProps={{ shrink: true }}
              InputProps={{ endAdornment: passwordAdornment }}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Update
          </LoadingButton>
        </DialogActions>
      </Form>
    </Dialog>
  );
}
